import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import {
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Target,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Filter,
  ArrowUpDown,
  Layers,
  Upload,
  MapPin,
} from "lucide-react";
import AdSpendCSVImport from "./AdSpendCSVImport";
import SpendEntryManager from "./SpendEntryManager";

// Formatters
const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;
const pctDec = (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`;
const currency = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
const currencyDec = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Low confidence threshold
const LOW_N_THRESHOLD = 20;

// Level labels
const LEVEL_LABELS = {
  campaign: "Campaign",
  adset: "Ad Set",
  ad: "Ad",
  form: "Form",
};

const NEXT_LEVEL = {
  campaign: "adset",
  adset: "ad",
  ad: "form",
  form: null,
};

// Valid verticals for mapping
const VALID_VERTICALS = [
  { value: "mortgage_protection", label: "Mortgage Protection" },
  { value: "final_expense", label: "Final Expense" },
  { value: "medicare", label: "Medicare" },
  { value: "term_life", label: "Term Life" },
  { value: "whole_life", label: "Whole Life" },
];

// Quick Map Dialog Component
function QuickMapDialog({ 
  open, 
  onOpenChange, 
  item, 
  level, 
  existingMapping, 
  onSuccess 
}) {
  const [selectedVertical, setSelectedVertical] = useState(existingMapping?.vertical || "");
  const [saving, setSaving] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedVertical(existingMapping?.vertical || "");
    }
  }, [open, existingMapping]);

  const mappingType = level === "form" ? "form" : "campaign";
  const entityId = level === "form" ? item?.meta_form_id : item?.meta_campaign_id;
  const entityName = item?.name || "Unknown";

  const handleSave = async () => {
    if (!selectedVertical) {
      toast({
        title: "Select a vertical",
        description: "Please select a vertical to map to.",
        variant: "destructive",
      });
      return;
    }

    // If editing existing, show confirmation
    if (existingMapping && existingMapping.vertical !== selectedVertical && !showEditConfirm) {
      setShowEditConfirm(true);
      return;
    }

    setSaving(true);
    try {
      if (existingMapping) {
        // Update existing mapping
        await api.put(`/vertical-mappings/${existingMapping.id}`, {
          vertical: selectedVertical,
        });
        toast({
          title: "Mapping updated",
          description: `${entityName} → ${VALID_VERTICALS.find(v => v.value === selectedVertical)?.label}`,
        });
      } else {
        // Create new mapping
        const payload = {
          platform: "meta",
          mapping_type: mappingType,
          vertical: selectedVertical,
        };
        if (mappingType === "campaign") {
          payload.meta_campaign_id = entityId;
        } else {
          payload.meta_form_id = entityId;
        }
        
        await api.post("/vertical-mappings", payload);
        toast({
          title: "Mapping created",
          description: `${entityName} → ${VALID_VERTICALS.find(v => v.value === selectedVertical)?.label}`,
        });
      }
      
      onSuccess?.();
      onOpenChange(false);
      setShowEditConfirm(false);
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to save mapping",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Edit confirmation dialog
  if (showEditConfirm) {
    return (
      <AlertDialog open={showEditConfirm} onOpenChange={setShowEditConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update existing mapping?</AlertDialogTitle>
            <AlertDialogDescription>
              This {mappingType} already has a mapping to "{existingMapping?.vertical}". 
              Do you want to change it to "{selectedVertical}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowEditConfirm(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave} disabled={saving}>
              {saving ? "Updating..." : "Update Mapping"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-map-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-indigo-400" />
            Map to Vertical
          </DialogTitle>
          <DialogDescription>
            {existingMapping 
              ? `Update vertical mapping for this ${mappingType}`
              : `Create a vertical mapping for this ${mappingType}. New leads will be auto-classified.`
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Entity Info */}
          <div className="rounded-lg bg-slate-800/50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400 uppercase">
                {LEVEL_LABELS[level]}
              </span>
              {existingMapping && (
                <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-300">
                  Has mapping
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium text-slate-100 truncate" title={entityName}>
              {entityName}
            </p>
            <p className="text-xs text-slate-500 font-mono truncate" title={entityId}>
              ID: {entityId}
            </p>
          </div>

          {/* Vertical Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-200">Vertical</label>
            <Select value={selectedVertical} onValueChange={setSelectedVertical}>
              <SelectTrigger data-testid="quick-map-vertical-select">
                <SelectValue placeholder="Select vertical..." />
              </SelectTrigger>
              <SelectContent>
                {VALID_VERTICALS.map(v => (
                  <SelectItem key={v.value} value={v.value}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {existingMapping && (
              <p className="text-xs text-slate-500">
                Current: {VALID_VERTICALS.find(v => v.value === existingMapping.vertical)?.label || existingMapping.vertical}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !selectedVertical}
            data-testid="quick-map-save-btn"
          >
            {saving ? "Saving..." : existingMapping ? "Update Mapping" : "Create Mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Spend coverage badge
function SpendCoverageBadge({ coverage }) {
  const styles = {
    complete: "bg-emerald-500/20 text-emerald-300",
    partial: "bg-amber-500/20 text-amber-300",
    none: "bg-slate-500/20 text-slate-400",
  };

  const labels = {
    complete: "Spend ✓",
    partial: "Partial",
    none: "No Spend",
  };

  return (
    <Badge className={`text-[10px] px-1.5 ${styles[coverage] || styles.none}`}>
      {labels[coverage] || "No Spend"}
    </Badge>
  );
}

// Low confidence indicator
function LowConfidenceBadge({ n }) {
  if (n >= LOW_N_THRESHOLD) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className="ml-1 bg-amber-500/20 text-amber-300 text-[10px] px-1 py-0">
            <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
            Low N
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            Sample size ({n}) is below threshold ({LOW_N_THRESHOLD}). Results may
            be unreliable.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Rate with sample size
function RateWithN({ rate, n, highlight = false }) {
  const rateStr = pctDec(rate);
  const isLow = n < LOW_N_THRESHOLD;

  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`font-mono ${
          highlight && rate >= 0.5
            ? "text-emerald-400"
            : highlight && rate < 0.3
            ? "text-red-400"
            : "text-slate-100"
        }`}
      >
        {rateStr}
      </span>
      <span className="text-slate-500 text-[10px]">({n})</span>
      {isLow && (
        <AlertTriangle className="h-3 w-3 text-amber-400 inline-block" />
      )}
    </span>
  );
}

// Breadcrumb navigation
function Breadcrumb({ path, onNavigate }) {
  return (
    <nav className="flex items-center gap-1 text-sm mb-4" data-testid="meta-breadcrumb">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onNavigate([])}
        className="h-7 px-2 text-slate-300 hover:text-slate-100 hover:bg-white/5"
      >
        <Home className="h-3.5 w-3.5 mr-1" />
        All Campaigns
      </Button>

      {path.map((item, idx) => (
        <React.Fragment key={idx}>
          <ChevronRight className="h-4 w-4 text-slate-500" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate(path.slice(0, idx + 1))}
            className={`h-7 px-2 ${
              idx === path.length - 1
                ? "text-indigo-300 font-medium"
                : "text-slate-300 hover:text-slate-100"
            } hover:bg-white/5`}
          >
            {item.name}
          </Button>
        </React.Fragment>
      ))}
    </nav>
  );
}

// Summary cards row
function SummaryCards({ data }) {
  if (!data) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
      <Card className="glass border-white/10">
        <CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase">Total Leads</p>
          <p className="text-lg font-bold text-slate-100">
            {data.total_leads?.toLocaleString() || 0}
          </p>
        </CardContent>
      </Card>

      <Card className="glass border-white/10">
        <CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase">Commission</p>
          <p className="text-lg font-bold text-emerald-400">
            {currency(data.total_commission)}
          </p>
        </CardContent>
      </Card>

      <Card className="glass border-white/10">
        <CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase">Ad Spend</p>
          <p className="text-lg font-bold text-slate-100">
            {data.total_spend ? currency(data.total_spend) : "—"}
          </p>
        </CardContent>
      </Card>

      <Card className="glass border-white/10">
        <CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase">Net Profit</p>
          <p
            className={`text-lg font-bold ${
              data.total_spend
                ? data.total_commission - data.total_spend >= 0
                  ? "text-emerald-400"
                  : "text-red-400"
                : "text-slate-400"
            }`}
          >
            {data.total_spend
              ? currency(data.total_commission - data.total_spend)
              : "—"}
          </p>
        </CardContent>
      </Card>

      <Card className="glass border-white/10">
        <CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase">
            {LEVEL_LABELS[data.level]}s
          </p>
          <p className="text-lg font-bold text-slate-100">
            {data.items?.length || 0}
          </p>
        </CardContent>
      </Card>

      <Card className="glass border-white/10">
        <CardContent className="p-3">
          <p className="text-[10px] text-slate-400 uppercase">Period</p>
          <p className="text-xs font-medium text-slate-300">
            {data.period_start?.slice(0, 10)} → {data.period_end?.slice(0, 10)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Benchmark status badge for table rows
function BenchmarkStatusBadge({ item, benchmarks }) {
  // Calculate overall status based on key KPIs
  const rplBenchmark = benchmarks?.revenue_per_lead;
  const cpaBenchmark = benchmarks?.cpa;
  
  let overallStatus = "none";
  
  // Check Revenue per Lead
  if (rplBenchmark?.target && item.revenue_per_lead !== undefined) {
    const variance = (item.revenue_per_lead - rplBenchmark.target) / rplBenchmark.target;
    if (item.revenue_per_lead >= rplBenchmark.target) {
      overallStatus = "green";
    } else if (Math.abs(variance) <= 0.25) {
      overallStatus = overallStatus === "red" ? "red" : "yellow";
    } else {
      overallStatus = "red";
    }
  }
  
  // Check CPA (lower is better) - if red, override
  if (cpaBenchmark?.target && item.cpa !== undefined && item.cpa > 0) {
    const variance = (item.cpa - cpaBenchmark.target) / cpaBenchmark.target;
    if (item.cpa > cpaBenchmark.target * 1.2) {
      overallStatus = "red";
    } else if (item.cpa > cpaBenchmark.target && overallStatus !== "red") {
      overallStatus = "yellow";
    }
  }
  
  if (overallStatus === "none") return null;
  
  const colors = {
    green: "bg-emerald-500/20 text-emerald-300",
    yellow: "bg-amber-500/20 text-amber-300",
    red: "bg-red-500/20 text-red-300",
  };
  
  return (
    <Badge className={`text-[10px] px-1.5 ${colors[overallStatus]}`}>
      {overallStatus === "green" && "✓"}
      {overallStatus === "yellow" && "⚠"}
      {overallStatus === "red" && "✗"}
    </Badge>
  );
}

// Main table
function HierarchyTable({
  items,
  level,
  onDrillDown,
  sortBy,
  onSortChange,
  coverageFilter,
  statusFilter,
  benchmarks,
  onQuickMap, // NEW: Quick map handler
  existingMappings, // NEW: Existing mappings for this level
}) {
  const nextLevel = NEXT_LEVEL[level];
  const canDrillDown = nextLevel !== null;
  
  // Check if this level supports quick mapping (campaign or form only)
  const canQuickMap = level === "campaign" || level === "form";

  // Calculate benchmark status for each item
  const itemsWithStatus = useMemo(() => {
    if (!items) return [];
    return items.map((item) => {
      const rplBenchmark = benchmarks?.revenue_per_lead;
      const cpaBenchmark = benchmarks?.cpa;
      
      let benchmarkStatus = "none";
      
      // Check Revenue per Lead
      if (rplBenchmark?.target && item.revenue_per_lead !== undefined) {
        const variance = (item.revenue_per_lead - rplBenchmark.target) / rplBenchmark.target;
        if (item.revenue_per_lead >= rplBenchmark.target) {
          benchmarkStatus = "green";
        } else if (Math.abs(variance) <= 0.25) {
          benchmarkStatus = "yellow";
        } else {
          benchmarkStatus = "red";
        }
      }
      
      // Check CPA (lower is better) - if red, override
      if (cpaBenchmark?.target && item.cpa !== undefined && item.cpa > 0) {
        const variance = (item.cpa - cpaBenchmark.target) / cpaBenchmark.target;
        if (item.cpa > cpaBenchmark.target * 1.2) {
          benchmarkStatus = "red";
        } else if (item.cpa > cpaBenchmark.target && benchmarkStatus !== "red") {
          benchmarkStatus = "yellow";
        }
      }
      
      return { ...item, benchmarkStatus };
    });
  }, [items, benchmarks]);

  // Filter by spend coverage
  const filteredItems = useMemo(() => {
    if (!itemsWithStatus) return [];
    let result = itemsWithStatus;
    
    // Coverage filter
    if (coverageFilter !== "all") {
      result = result.filter((item) => item.spend_coverage === coverageFilter);
    }
    
    // Status filter
    if (statusFilter && statusFilter !== "all") {
      if (statusFilter === "yellow") {
        result = result.filter((item) => item.benchmarkStatus === "yellow" || item.benchmarkStatus === "red");
      } else {
        result = result.filter((item) => item.benchmarkStatus === statusFilter);
      }
    }
    
    return result;
  }, [itemsWithStatus, coverageFilter, statusFilter]);

  // Sort items
  const sortedItems = useMemo(() => {
    if (!filteredItems) return [];
    const sorted = [...filteredItems];

    switch (sortBy) {
      case "leads":
        sorted.sort((a, b) => b.leads - a.leads);
        break;
      case "revenue_per_lead":
        sorted.sort((a, b) => b.revenue_per_lead - a.revenue_per_lead);
        break;
      case "commission":
        sorted.sort((a, b) => b.commission - a.commission);
        break;
      case "net_profit":
        sorted.sort(
          (a, b) => (b.net_profit || -Infinity) - (a.net_profit || -Infinity)
        );
        break;
      case "cpa":
        sorted.sort(
          (a, b) => (a.cpa || Infinity) - (b.cpa || Infinity)
        );
        break;
      case "issue_rate":
        sorted.sort((a, b) => b.issue_rate - a.issue_rate);
        break;
      default:
        break;
    }

    return sorted;
  }, [filteredItems, sortBy]);

  if (!items || items.length === 0) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="p-8 text-center">
          <Layers className="h-10 w-10 mx-auto mb-3 text-slate-500" />
          <p className="text-slate-300">No data available for this level.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="meta-hierarchy-table">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base text-slate-100 flex items-center gap-2">
            <Target className="h-4 w-4 text-indigo-400" />
            {LEVEL_LABELS[level]} Performance
            <Badge className="ml-2 bg-slate-600/50 text-slate-300 text-xs">
              {filteredItems.length} of {items.length}
            </Badge>
          </CardTitle>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Sort:</span>
            <Select value={sortBy} onValueChange={onSortChange}>
              <SelectTrigger className="w-[140px] h-7 bg-white/5 text-slate-100 border-white/10 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leads">Most Leads</SelectItem>
                <SelectItem value="revenue_per_lead">Best Rev/Lead</SelectItem>
                <SelectItem value="commission">Top Commission</SelectItem>
                <SelectItem value="net_profit">Best Net Profit</SelectItem>
                <SelectItem value="cpa">Best CPA</SelectItem>
                <SelectItem value="issue_rate">Best Issue Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-slate-300">Name</TableHead>
                <TableHead className="text-slate-300 text-right">Leads</TableHead>
                <TableHead className="text-slate-300 text-right">L→B</TableHead>
                <TableHead className="text-slate-300 text-right">Show</TableHead>
                <TableHead className="text-slate-300 text-right">Conv</TableHead>
                <TableHead className="text-slate-300 text-right">Issue</TableHead>
                <TableHead className="text-slate-300 text-right">Rev/Lead</TableHead>
                <TableHead className="text-slate-300 text-right">Commission</TableHead>
                <TableHead className="text-slate-300 text-right">Spend</TableHead>
                <TableHead className="text-slate-300 text-right">CPA</TableHead>
                <TableHead className="text-slate-300 text-right">Net</TableHead>
                <TableHead className="text-slate-300 text-center">Status</TableHead>
                {canQuickMap && (
                  <TableHead className="text-slate-300 text-center w-[60px]">Map</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((item) => {
                const itemKey =
                  item.meta_form_id ||
                  item.meta_ad_id ||
                  item.meta_adset_id ||
                  item.meta_campaign_id;
                
                // Status color for benchmark
                const statusColors = {
                  green: "bg-emerald-500/20 text-emerald-300",
                  yellow: "bg-amber-500/20 text-amber-300",
                  red: "bg-red-500/20 text-red-300",
                  none: "bg-slate-500/20 text-slate-400",
                };

                return (
                  <TableRow
                    key={itemKey}
                    className="border-white/10 hover:bg-white/5 cursor-pointer"
                    onClick={() => canDrillDown && onDrillDown(item)}
                  >
                    <TableCell className="max-w-[200px]">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p
                            className={`text-sm truncate ${
                              canDrillDown
                                ? "text-indigo-300 hover:text-indigo-200"
                                : "text-slate-100"
                            }`}
                            title={item.name}
                          >
                            {item.name}
                          </p>
                          {canDrillDown && (
                            <ChevronRight className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <SpendCoverageBadge coverage={item.spend_coverage} />
                          {item.child_count > 0 && (
                            <Badge className="text-[10px] bg-slate-600/50 text-slate-300">
                              {item.child_count} {LEVEL_LABELS[nextLevel]}s
                            </Badge>
                          )}
                          <LowConfidenceBadge n={item.leads} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-100">
                      {item.leads}
                    </TableCell>
                    <TableCell className="text-right">
                      <RateWithN
                        rate={item.lead_to_booked_rate}
                        n={item.leads}
                        highlight
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <RateWithN
                        rate={item.show_rate}
                        n={item.booked}
                        highlight
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <RateWithN
                        rate={item.sales_conversion_rate}
                        n={item.held}
                        highlight
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <RateWithN
                        rate={item.issue_rate}
                        n={item.submitted}
                        highlight
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-400">
                      {currencyDec(item.revenue_per_lead)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-100">
                      {currency(item.commission)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-100">
                      {item.ad_spend ? currency(item.ad_spend) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-100">
                      {item.cpa ? currencyDec(item.cpa) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.net_profit !== null && item.net_profit !== undefined ? (
                        <span
                          className={`font-mono ${
                            item.net_profit >= 0
                              ? "text-emerald-400"
                              : "text-red-400"
                          }`}
                        >
                          {currency(item.net_profit)}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`text-[10px] px-1.5 ${statusColors[item.benchmarkStatus] || statusColors.none}`}>
                        {item.benchmarkStatus === "green" && "✓"}
                        {item.benchmarkStatus === "yellow" && "⚠"}
                        {item.benchmarkStatus === "red" && "✗"}
                        {item.benchmarkStatus === "none" && "-"}
                      </Badge>
                    </TableCell>
                    {canQuickMap && (
                      <TableCell className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`h-7 w-7 p-0 ${
                                  existingMappings?.[itemKey]
                                    ? "text-emerald-400 hover:text-emerald-300"
                                    : "text-slate-400 hover:text-indigo-300"
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onQuickMap?.(item, existingMappings?.[itemKey] || null);
                                }}
                                data-testid={`quick-map-btn-${itemKey}`}
                              >
                                <MapPin className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {existingMappings?.[itemKey] 
                                ? `Mapped: ${existingMappings[itemKey].vertical}`
                                : "Map to Vertical"
                              }
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {sortedItems.length === 0 && (
          <p className="text-sm text-slate-400 mt-4 text-center">No items match the selected filters.</p>
        )}
      </CardContent>
    </Card>
  );
}

// Main Component
export default function MetaDrilldown({ period, vertical, campaignId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [benchmarks, setBenchmarks] = useState({});

  // Navigation state
  const [navPath, setNavPath] = useState([]); // [{level, id, name}, ...]

  // Filter/sort state
  const [minLeads, setMinLeads] = useState(10);
  const [coverageFilter, setCoverageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // NEW: Benchmark status filter
  const [sortBy, setSortBy] = useState("leads");
  
  // Quick Map state
  const [quickMapOpen, setQuickMapOpen] = useState(false);
  const [quickMapItem, setQuickMapItem] = useState(null);
  const [quickMapExisting, setQuickMapExisting] = useState(null);
  const [existingMappings, setExistingMappings] = useState({}); // keyed by campaign_id or form_id

  // Current level based on navigation
  const currentLevel = useMemo(() => {
    if (navPath.length === 0) return "campaign";
    const lastItem = navPath[navPath.length - 1];
    return NEXT_LEVEL[lastItem.level] || "form";
  }, [navPath]);

  // Build query params based on navigation
  const queryParams = useMemo(() => {
    const params = { level: currentLevel };

    if (navPath.length > 0) {
      for (const item of navPath) {
        if (item.level === "campaign") {
          params.meta_campaign_id = item.id;
        } else if (item.level === "adset") {
          params.meta_adset_id = item.id;
        } else if (item.level === "ad") {
          params.meta_ad_id = item.id;
        }
      }
    }

    return params;
  }, [navPath, currentLevel]);
  
  // Fetch existing vertical mappings
  const fetchMappings = useCallback(async () => {
    try {
      const res = await api.get('/vertical-mappings');
      const mappings = res.data?.mappings || [];
      
      // Index by campaign_id or form_id for quick lookup
      const indexed = {};
      for (const m of mappings) {
        if (m.mapping_type === "campaign" && m.meta_campaign_id) {
          indexed[m.meta_campaign_id] = m;
        } else if (m.mapping_type === "form" && m.meta_form_id) {
          indexed[m.meta_form_id] = m;
        }
      }
      setExistingMappings(indexed);
    } catch (e) {
      console.error("Failed to fetch vertical mappings:", e);
    }
  }, []);
  
  // Fetch mappings on mount and when needed
  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);
  
  // Handle Quick Map click
  const handleQuickMap = useCallback((item, existing) => {
    setQuickMapItem(item);
    setQuickMapExisting(existing);
    setQuickMapOpen(true);
  }, []);
  
  // Handle Quick Map success - refresh data and mappings
  const handleQuickMapSuccess = useCallback(async () => {
    await fetchMappings();
    // Also refresh the hierarchy data
    // Note: This will trigger a re-fetch via the useEffect
  }, [fetchMappings]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const searchParams = new URLSearchParams();
        searchParams.set("level", queryParams.level);

        if (queryParams.meta_campaign_id) {
          searchParams.set("meta_campaign_id", queryParams.meta_campaign_id);
        }
        if (queryParams.meta_adset_id) {
          searchParams.set("meta_adset_id", queryParams.meta_adset_id);
        }
        if (queryParams.meta_ad_id) {
          searchParams.set("meta_ad_id", queryParams.meta_ad_id);
        }

        // Add date range based on period
        const now = new Date();
        let startDate = new Date();

        if (period === "7d") {
          startDate.setDate(now.getDate() - 7);
        } else if (period === "30d") {
          startDate.setDate(now.getDate() - 30);
        } else if (period === "mtd") {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        } else if (period === "ytd") {
          startDate = new Date(now.getFullYear(), 0, 1);
        } else {
          startDate.setDate(now.getDate() - 30);
        }

        searchParams.set("start_date", startDate.toISOString().slice(0, 10));
        searchParams.set("end_date", now.toISOString().slice(0, 10));
        
        // Add vertical filter if specified
        if (vertical && vertical !== "all") {
          searchParams.set("vertical", vertical);
        }
        
        // MA-IMPL-118B: Add campaign filter if specified
        if (campaignId && campaignId !== "all") {
          searchParams.set("campaign_id", campaignId);
        }

        // Fetch both hierarchy data and benchmarks in parallel
        const verticalParam = vertical && vertical !== "all" ? `&vertical=${vertical}` : "";
        const campaignParam = campaignId && campaignId !== "all" ? `&campaign_id=${campaignId}` : "";
        const [hierarchyRes, benchmarkRes] = await Promise.all([
          api.get(`/analytics/meta/hierarchy?${searchParams.toString()}`),
          api.get(`/analytics/benchmarks/evaluate?format=structured${verticalParam}${campaignParam}`),
        ]);
        
        setData(hierarchyRes.data);
        
        // Convert benchmark array to map
        const benchmarkMap = {};
        for (const kpi of benchmarkRes.data?.kpis || []) {
          benchmarkMap[kpi.kpi] = kpi;
        }
        setBenchmarks(benchmarkMap);
      } catch (e) {
        console.error("Failed to fetch meta hierarchy:", e);
        setError(e.response?.data?.detail || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [queryParams, period, vertical, campaignId]);

  // Refetch function for after CSV import
  const refetchData = useCallback(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const searchParams = new URLSearchParams();
        searchParams.set("level", queryParams.level);

        if (queryParams.meta_campaign_id) {
          searchParams.set("meta_campaign_id", queryParams.meta_campaign_id);
        }
        if (queryParams.meta_adset_id) {
          searchParams.set("meta_adset_id", queryParams.meta_adset_id);
        }
        if (queryParams.meta_ad_id) {
          searchParams.set("meta_ad_id", queryParams.meta_ad_id);
        }

        const now = new Date();
        let startDate = new Date();
        if (period === "7d") startDate.setDate(now.getDate() - 7);
        else if (period === "30d") startDate.setDate(now.getDate() - 30);
        else if (period === "mtd") startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        else if (period === "ytd") startDate = new Date(now.getFullYear(), 0, 1);
        else startDate.setDate(now.getDate() - 30);

        searchParams.set("start_date", startDate.toISOString().slice(0, 10));
        searchParams.set("end_date", now.toISOString().slice(0, 10));

        const res = await api.get(`/analytics/meta/hierarchy?${searchParams.toString()}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [queryParams, period]);

  // Filter by min leads
  const filteredData = useMemo(() => {
    if (!data) return null;
    return {
      ...data,
      items: data.items?.filter((item) => item.leads >= minLeads) || [],
    };
  }, [data, minLeads]);

  // Handle drill-down
  const handleDrillDown = (item) => {
    // Use the correct ID based on the current level
    let id;
    if (currentLevel === "campaign") {
      id = item.meta_campaign_id;
    } else if (currentLevel === "adset") {
      id = item.meta_adset_id;
    } else if (currentLevel === "ad") {
      id = item.meta_ad_id;
    } else {
      id = item.meta_form_id;
    }

    const newPathItem = {
      level: currentLevel,
      id,
      name: item.name,
    };

    setNavPath([...navPath, newPathItem]);
  };

  // Handle breadcrumb navigation
  const handleNavigate = (newPath) => {
    setNavPath(newPath);
  };

  return (
    <div className="space-y-4" data-testid="meta-drilldown">
      {/* Controls */}
      <Card className="glass border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Min Leads Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-300">Min Leads:</span>
              <Input
                type="number"
                value={minLeads}
                onChange={(e) => setMinLeads(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 h-8 bg-white/5 text-slate-100 border-white/10 text-sm"
                min={1}
              />
            </div>

            {/* Spend Coverage Filter */}
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-300">Spend:</span>
              <Select value={coverageFilter} onValueChange={setCoverageFilter}>
                <SelectTrigger className="w-[120px] h-8 bg-white/5 text-slate-100 border-white/10 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="complete">With Spend</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="none">No Spend</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Benchmark Status Filter */}
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-300">Status:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px] h-8 bg-white/5 text-slate-100 border-white/10 text-xs" data-testid="meta-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="red">Critical Only</SelectItem>
                  <SelectItem value="yellow">Warning & Critical</SelectItem>
                  <SelectItem value="green">On Target</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Current Level Indicator */}
            <div className="ml-auto flex items-center gap-2">
              <SpendEntryManager onDataChange={refetchData} />
              <AdSpendCSVImport onImportComplete={refetchData} />
              <Badge className="bg-indigo-500/20 text-indigo-300">
                {LEVEL_LABELS[currentLevel]} View
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Breadcrumb */}
      {navPath.length > 0 && (
        <Breadcrumb path={navPath} onNavigate={handleNavigate} />
      )}

      {/* Loading / Error */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
        </div>
      ) : error ? (
        <Card className="glass border-white/10">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-red-400" />
            <p className="text-slate-300">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <SummaryCards data={filteredData} />

          {/* Main Table */}
          <HierarchyTable
            items={filteredData?.items}
            level={currentLevel}
            onDrillDown={handleDrillDown}
            sortBy={sortBy}
            onSortChange={setSortBy}
            coverageFilter={coverageFilter}
            statusFilter={statusFilter}
            benchmarks={benchmarks}
            onQuickMap={handleQuickMap}
            existingMappings={existingMappings}
          />
          
          {/* Quick Map Dialog */}
          <QuickMapDialog
            open={quickMapOpen}
            onOpenChange={setQuickMapOpen}
            item={quickMapItem}
            level={currentLevel}
            existingMapping={quickMapExisting}
            onSuccess={handleQuickMapSuccess}
          />
        </>
      )}
    </div>
  );
}
